import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BadgeCheck, CheckCircle2, FileSearch, Handshake, Percent, ShieldCheck, Sparkles, Wallet } from 'lucide-react';
import PageHero from '../../components/PageHero';
import api from '../../services/api';
import { useAppSelector } from '../../hooks/redux';

type Pricing = {
  countryCode: string;
  countryName: string;
  currency: string;
  ratingFee: number;
  successFeePercent: number;
  minSuccessFee: number;
  notes: string | null;
};

const RAISE_STEPS: Record<string, number[]> = {
  INR: [2_500_000, 5_000_000, 10_000_000, 25_000_000, 50_000_000, 100_000_000, 250_000_000, 500_000_000],
  DEFAULT: [100_000, 250_000, 500_000, 1_000_000, 2_500_000, 5_000_000, 10_000_000, 25_000_000],
};

const INCLUDED = [
  { icon: FileSearch, title: 'AI document verification', desc: 'Every upload checked against your country checklist.' },
  { icon: BadgeCheck, title: 'Lender-grade credit rating', desc: 'Grade, score, outlook and a full report you can share.' },
  { icon: Sparkles, title: 'Remediation plan', desc: 'Exactly what to fix to lift your rating before you raise.' },
  { icon: Handshake, title: 'Partner introductions', desc: 'Matched to lenders and investors with appetite for your profile.' },
];

function money(amount: number, currency: string, compact = false) {
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: compact ? 1 : 0,
      ...(compact ? { notation: 'compact' as const } : {}),
    }).format(amount);
  } catch {
    return `${currency} ${amount.toLocaleString()}`;
  }
}

const regionLabel = (p: Pricing) => (p.countryCode === '*' ? 'International' : p.countryName);

export default function PricingPage() {
  const countryCode = useAppSelector((s) => s.geo.countryCode);
  const [schedules, setSchedules] = useState<Pricing[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [step, setStep] = useState(3);

  useEffect(() => {
    let alive = true;
    setStatus('loading');
    api
      .get<{ pricing: Pricing | null; schedules: Pricing[] }>('/pricing', { params: countryCode === 'DEFAULT' ? {} : { country: countryCode } })
      .then(({ data }) => {
        if (!alive) return;
        const list = [...data.schedules].sort((a, b) => (a.countryCode === '*' ? 1 : b.countryCode === '*' ? -1 : a.countryName.localeCompare(b.countryName)));
        setSchedules(list);
        setSelected(data.pricing?.countryCode || list[0]?.countryCode || null);
        setStatus('ready');
      })
      .catch(() => alive && setStatus('error'));
    return () => {
      alive = false;
    };
  }, [countryCode]);

  const p = schedules.find((s) => s.countryCode === selected) || null;
  const steps = RAISE_STEPS[p?.currency || ''] || RAISE_STEPS.DEFAULT;
  const raise = steps[Math.min(step, steps.length - 1)];
  const calc = useMemo(() => {
    if (!p) return null;
    const success = Math.max(p.minSuccessFee, (raise * p.successFeePercent) / 100);
    const total = p.ratingFee + success;
    return { success, total, effective: (total / raise) * 100 };
  }, [p, raise]);

  return (
    <>
      <PageHero
        badge="Pricing"
        title="Pay once to get rated."
        titleAccent="Pay us more only when you raise."
        subtitle="A one-time processing fee covers verification, your credit rating and a remediation plan. Our success fee is charged only on funding you actually receive through Micus."
      />
      <section style={{ padding: '48px 0 24px', background: '#FFFBF8' }}>
        <div className="container-wide">
          {schedules.length > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
              <div role="tablist" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4, padding: 4, borderRadius: 999, background: 'white', border: '1px solid #F0C8D8', boxShadow: '0 6px 24px rgba(194,24,91,0.06)' }}>
                {schedules.map((s) => {
                  const on = s.countryCode === selected;
                  return (
                    <button
                      key={s.countryCode}
                      type="button"
                      role="tab"
                      aria-selected={on}
                      onClick={() => setSelected(s.countryCode)}
                      style={{
                        padding: '8px 18px',
                        borderRadius: 999,
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 13.5,
                        fontWeight: on ? 800 : 600,
                        color: on ? 'white' : '#6B3A52',
                        background: on ? 'linear-gradient(135deg, #C2185B, #E91E63)' : 'transparent',
                        transition: 'all .2s',
                      }}
                    >
                      {regionLabel(s)} · {s.currency}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {status === 'loading' && !p ? (
            <div style={{ height: 320, borderRadius: 28, background: 'linear-gradient(90deg,#FFF0F5,#FFFBF8,#FFF0F5)', border: '1px solid #F0C8D8' }} />
          ) : status === 'error' || !p ? (
            <div style={{ textAlign: 'center', padding: '48px 24px', background: 'white', borderRadius: 24, border: '1px solid #F0C8D8' }}>
              <p style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Pricing is being updated.</p>
              <p style={{ margin: '6px 0 16px', color: '#6B3A52', fontSize: 14 }}>Talk to our team for a quote for your market.</p>
              <Link to="/contact" style={{ color: '#C2185B', fontWeight: 800, textDecoration: 'none' }}>
                Contact us →
              </Link>
            </div>
          ) : (
            <motion.div
              key={p.countryCode}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18, alignItems: 'stretch' }}
            >
              <div style={{ background: 'white', borderRadius: 28, border: '1px solid #F0C8D8', padding: 32, boxShadow: '0 16px 50px rgba(194,24,91,0.08)', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: '#FFF0F5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Wallet size={18} color="#C2185B" />
                  </div>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#C2185B' }}>Step 1 · Processing fee</p>
                </div>
                <p style={{ margin: '22px 0 0', fontSize: 'clamp(2.6rem, 6vw, 3.6rem)', fontWeight: 900, letterSpacing: '-0.04em', color: '#1A0A10', lineHeight: 1 }}>{money(p.ratingFee, p.currency)}</p>
                <p style={{ margin: '8px 0 0', fontSize: 14, color: '#6B3A52' }}>One-time, per company · {regionLabel(p)}</p>
                <ul style={{ listStyle: 'none', margin: '24px 0 0', padding: 0, display: 'grid', gap: 10 }}>
                  {['Document checklist for your country', 'AI verification of every document', 'Credit rating with full report', 'Remediation plan and re-rating'].map((t) => (
                    <li key={t} style={{ display: 'flex', gap: 10, fontSize: 14, color: '#4A2535' }}>
                      <CheckCircle2 size={17} color="#1B8A5A" style={{ flexShrink: 0, marginTop: 1 }} /> {t}
                    </li>
                  ))}
                </ul>
                <div style={{ flex: 1 }} />
                <Link
                  to="/#start"
                  style={{ marginTop: 28, display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: 8, padding: '14px 22px', borderRadius: 999, background: 'linear-gradient(135deg, #C2185B, #E91E63)', color: 'white', fontWeight: 800, fontSize: 15, textDecoration: 'none', boxShadow: '0 8px 24px rgba(233,30,99,0.3)' }}
                >
                  Start my rating <ArrowRight size={16} />
                </Link>
              </div>

              <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 28, padding: 32, color: 'white', background: 'linear-gradient(145deg, #2D0A1A 0%, #880E4F 55%, #C2185B 100%)', boxShadow: '0 20px 60px rgba(136,14,79,0.28)', display: 'flex', flexDirection: 'column' }}>
                <div style={{ position: 'absolute', right: -60, top: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, position: 'relative' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(255,255,255,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Percent size={18} color="white" />
                  </div>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.8 }}>Step 2 · Success fee</p>
                </div>
                <p style={{ margin: '22px 0 0', fontSize: 'clamp(2.6rem, 6vw, 3.6rem)', fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 1, position: 'relative' }}>
                  {p.successFeePercent}%
                </p>
                <p style={{ margin: '8px 0 0', fontSize: 14, opacity: 0.8, position: 'relative' }}>
                  of total funding raised{p.minSuccessFee > 0 ? ` · minimum ${money(p.minSuccessFee, p.currency)}` : ''}
                </p>
                <div style={{ marginTop: 24, padding: 18, borderRadius: 18, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.16)', position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
                    <span style={{ fontSize: 12.5, opacity: 0.8 }}>If you raise</span>
                    <span style={{ fontSize: 22, fontWeight: 900 }}>{money(raise, p.currency, true)}</span>
                  </div>
                  <input
                    type="range"
                    aria-label="Funding raised"
                    min={0}
                    max={steps.length - 1}
                    value={Math.min(step, steps.length - 1)}
                    onChange={(e) => setStep(Number(e.target.value))}
                    style={{ width: '100%', margin: '14px 0 12px', accentColor: '#FFB3D1' }}
                  />
                  {calc && (
                    <div style={{ display: 'grid', gap: 6, fontSize: 13.5 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ opacity: 0.8 }}>Success fee</span>
                        <strong>{money(calc.success, p.currency)}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ opacity: 0.8 }}>Processing fee</span>
                        <strong>{money(p.ratingFee, p.currency)}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 8, marginTop: 2 }}>
                        <span>Total cost of raise</span>
                        <strong style={{ fontSize: 16 }}>
                          {money(calc.total, p.currency)} <span style={{ fontSize: 12, opacity: 0.75, fontWeight: 600 }}>({calc.effective.toFixed(1)}%)</span>
                        </strong>
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ flex: 1 }} />
                <p style={{ margin: '20px 0 0', display: 'flex', gap: 8, fontSize: 13, opacity: 0.85, position: 'relative' }}>
                  <ShieldCheck size={16} style={{ flexShrink: 0 }} /> No raise, no success fee. Charged only when funds reach your account.
                </p>
              </div>
            </motion.div>
          )}

          {p?.notes && <p style={{ margin: '16px auto 0', maxWidth: 720, textAlign: 'center', fontSize: 13, color: '#9B6E7E' }}>{p.notes}</p>}
          {p && <p style={{ margin: '10px 0 0', textAlign: 'center', fontSize: 12, color: '#9B6E7E' }}>Prices exclude applicable taxes.</p>}
        </div>
      </section>

      <section style={{ padding: '48px 0 72px', background: '#FFFBF8' }}>
        <div className="container-wide">
          <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#C2185B', textAlign: 'center' }}>Everything included</p>
          <h2 style={{ margin: '8px 0 28px', fontSize: 28, fontWeight: 900, letterSpacing: '-0.02em', textAlign: 'center' }}>One journey from documents to capital</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 16 }}>
            {INCLUDED.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                style={{ background: 'white', borderRadius: 18, border: '1px solid #F0C8D8', padding: 22 }}
              >
                <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg,#C2185B,#E91E63)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                  <Icon size={18} color="#fff" />
                </div>
                <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 800 }}>{title}</h3>
                <p style={{ margin: 0, fontSize: 14, color: '#6B3A52', lineHeight: 1.6 }}>{desc}</p>
              </motion.div>
            ))}
          </div>
          <p style={{ margin: '28px 0 0', textAlign: 'center', fontSize: 14, color: '#6B3A52' }}>
            Questions about pricing for your market?{' '}
            <Link to="/contact" style={{ color: '#C2185B', fontWeight: 800, textDecoration: 'none' }}>
              Talk to us
            </Link>
          </p>
        </div>
      </section>
    </>
  );
}
